import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageCover, PageBody, CoverButton, SheetHeader, StatusDot } from '@/components/Page';
import { formatNumero } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import { FILIAIS_TINY } from '@/lib/filiais';

const ROLE_LABEL = {
  master: 'Master', comercial: 'Comercial', administrativo: 'Administrativo', sales_ops: 'Sales Ops',
  financiamentos: 'Financiamentos',
};

// Cada filial é uma conta do Tiny, com IDs de vendedor próprios (o despacho
// aprende e grava o ID de cada filial pelo nome; aqui o master só corrige).
const campoFilial = (n) => `tiny_consultor_id_empresa_${n}`;
const idsPorFilial = (origem, valor) => Object.fromEntries(FILIAIS_TINY.map(([n]) => [campoFilial(n), valor(origem?.[campoFilial(n)])]));

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);
  // Contas Google aguardando o Master vincular a um perfil (primeiro acesso).
  const [pendentes, setPendentes] = useState([]);
  // Escolhas por solicitação: { [sub]: { role, user_id } }
  const [vinculo, setVinculo] = useState({});
  const [vinculando, setVinculando] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'comercial',
    tiny_vendedor_id: '',
    tiny_vendedor_nome: '',
    ...idsPorFilial(null, () => ''),
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendentes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users/google-pendentes`);
      setPendentes(Array.isArray(response.data) ? response.data : []);
    } catch {
      /* seção some se não carregar — não bloqueia a tela */
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendentes();
  }, [fetchUsers, fetchPendentes]);

  const handleVincular = async (sub) => {
    const escolha = vinculo[sub] || {};
    if (!escolha.user_id && !escolha.role) {
      toast.error('Escolha um perfil ou um usuário existente');
      return;
    }
    setVinculando(sub);
    try {
      const body = escolha.user_id ? { user_id: escolha.user_id } : { role: escolha.role };
      await axios.post(`${API_URL}/users/google-pendentes/${sub}/vincular`, body);
      toast.success('Conta Google vinculada. O usuário já pode entrar.');
      fetchUsers();
      fetchPendentes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular conta Google');
    } finally {
      setVinculando(null);
    }
  };

  const handleNegar = async (sub) => {
    setVinculando(sub);
    try {
      await axios.post(`${API_URL}/users/google-pendentes/${sub}/negar`);
      toast.success('Acesso negado. A pessoa não volta para a lista ao tentar entrar de novo.');
      fetchPendentes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao negar o acesso');
    } finally {
      setVinculando(null);
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'comercial',
      tiny_vendedor_id: '',
      tiny_vendedor_nome: '',
      ...idsPorFilial(null, () => ''),
    });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (u) => {
    setSelectedUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role,
      tiny_vendedor_id: u.tiny_vendedor_id || '',
      tiny_vendedor_nome: u.tiny_vendedor_nome || '',
      ...idsPorFilial(u, (v) => v || ''),
      webhook_url: u.webhook_url || '',
      webhook_segredo: '',
    });
    setEditDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Preencha nome, email e senha');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        tiny_vendedor_id: (formData.tiny_vendedor_id || '').trim() || null,
        tiny_vendedor_nome: (formData.tiny_vendedor_nome || '').trim() || null,
        ...idsPorFilial(formData, (v) => (v || '').trim() || null),
      };
      await axios.post(`${API_URL}/users`, payload);
      toast.success('Usuário criado com sucesso!');
      setAddDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        tiny_vendedor_id: (formData.tiny_vendedor_id || '').trim() || null,
        tiny_vendedor_nome: (formData.tiny_vendedor_nome || '').trim() || null,
        // Só os IDs por filial que mudaram: o despacho pode ter aprendido um ID
        // depois que este formulário abriu, e reenviar o valor velho o apagaria.
        ...Object.fromEntries(FILIAIS_TINY.map(([n]) => campoFilial(n))
          .filter((c) => (formData[c] || '').trim() !== (selectedUser?.[c] || ''))
          .map((c) => [c, (formData[c] || '').trim()])),
      };
      if (formData.password) payload.password = formData.password;
      // URL só quando muda: reenviar a mesma não recomeça nada, mas evita gravar à toa.
      const webhookUrl = (formData.webhook_url || '').trim();
      if (webhookUrl !== (selectedUser?.webhook_url || '')) payload.webhook_url = webhookUrl;
      if (formData.webhook_segredo) payload.webhook_segredo = formData.webhook_segredo;

      await axios.patch(`${API_URL}/users/${selectedUser.id}`, payload);
      toast.success('Usuário atualizado com sucesso!');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.user) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/users/${deleteDialog.user.id}`);
      toast.success('Usuário deletado com sucesso!');
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar usuário');
    } finally {
      setDeleting(false);
    }
  };

  const renderVendedorField = (idPrefix) => (
    <div className="pt-4 border-t space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}vendedor_nome`}>Nome do vendedor no Tiny</Label>
        <Input
          id={`${idPrefix}vendedor_nome`}
          value={formData.tiny_vendedor_nome}
          onChange={(e) => setFormData({ ...formData, tiny_vendedor_nome: e.target.value })}
          placeholder="Ex: Douglas Ogregon"
        />
        <p className="text-xs text-muted-foreground">
          Nome <strong>exatamente</strong> como cadastrado no Tiny. É o que
          aparece no campo "Vendedor" do pedido criado lá. Se deixar vazio,
          o sistema usa o "Nome Completo" do usuário BCMED como fallback.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}vendedor_id`}>ID padrão do vendedor no Tiny</Label>
        <Input
          id={`${idPrefix}vendedor_id`}
          type="number"
          value={formData.tiny_vendedor_id}
          onChange={(e) => setFormData({ ...formData, tiny_vendedor_id: e.target.value })}
          placeholder="Ex: 927867439"
        />
        <p className="text-xs text-muted-foreground">
          Usado quando a filial de destino não tem ID próprio (abaixo). Hoje é
          o da conta da Filial 5.
        </p>
      </div>

      <div className="space-y-2">
        <Label>ID do vendedor em cada filial do Tiny</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FILIAIS_TINY.map(([n, nome]) => (
            <div key={n} className="space-y-1">
              <Label htmlFor={`${idPrefix}${campoFilial(n)}`} className="text-xs text-muted-foreground">{nome}</Label>
              <Input
                id={`${idPrefix}${campoFilial(n)}`}
                inputMode="numeric"
                value={formData[campoFilial(n)] || ''}
                onChange={(e) => setFormData({ ...formData, [campoFilial(n)]: e.target.value.replace(/\D/g, '') })}
                placeholder="—"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Cada filial é uma conta do Tiny, com IDs próprios. O sistema preenche
          sozinho no primeiro pedido que vai para cada filial, achando o
          vendedor pelo nome acima; só edite para corrigir. Se o vendedor não
          estiver cadastrado no Tiny da filial, o pedido entra sem vendedor e o
          histórico do pedido avisa.
        </p>
      </div>
    </div>
  );

  if (user?.role !== 'master') {
    return (
      <div>
        <PageCover title="Usuários" subtitle="Acesso restrito" />
        <PageBody>
          <Card>
            <p className="px-5 py-10 text-sm text-gray-600 dark:text-gray-300">Só usuários master gerenciam usuários.</p>
          </Card>
        </PageBody>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageCover title="Usuários" subtitle="Carregando" />
        <PageBody>
          <Card>
            <ul aria-hidden className="divide-y divide-border">
              {[1, 2, 3, 4].map((i) => (
                <li key={i} className="space-y-2 px-5 py-4">
                  <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-64 animate-pulse rounded bg-muted" />
                </li>
              ))}
            </ul>
          </Card>
        </PageBody>
      </div>
    );
  }

  const comerciais = users.filter((u) => u.role === 'comercial').length;

  return (
    <div>
      <PageCover
        title="Usuários"
        subtitle="Controle de acesso e vínculo com o Tiny"
        actions={
          <CoverButton primary onClick={handleOpenAddDialog}>
            <Plus /> Novo usuário
          </CoverButton>
        }
        figures={[
          { value: formatNumero(users.length), label: users.length === 1 ? 'usuário' : 'usuários' },
          { value: formatNumero(comerciais), label: comerciais === 1 ? 'comercial' : 'comerciais' },
          ...(pendentes.length > 0 ? [{ value: formatNumero(pendentes.length), label: 'aguardando vínculo' }] : []),
        ]}
      />
      <PageBody>
        {pendentes.length > 0 && (
          <Card className="overflow-hidden">
            <SheetHeader title="Acessos Google aguardando vínculo">
              <StatusDot tone="att">{pendentes.length === 1 ? '1 pessoa esperando' : `${pendentes.length} pessoas esperando`}</StatusDot>
            </SheetHeader>
            <p className="px-5 pt-4 text-[13px] text-gray-500">
              Primeiro acesso pelo Google: escolha um perfil para criar o usuário ou vincule a um usuário que já existe.
            </p>
            <ul className="divide-y divide-border">
              {pendentes.map((p) => {
                const escolha = vinculo[p.sub] || {};
                return (
                  <li key={p.sub} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{p.name}</p>
                      <p className="truncate text-[13px] text-gray-500">{p.email}</p>
                    </div>
                    <Select
                      value={escolha.role || ''}
                      onValueChange={(v) => setVinculo({ ...vinculo, [p.sub]: { role: v, user_id: '' } })}
                    >
                      <SelectTrigger className="w-full md:w-44" data-testid={`select-role-${p.sub}`}>
                        <SelectValue placeholder="Criar com perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comercial">Comercial</SelectItem>
                        <SelectItem value="administrativo">Administrativo</SelectItem>
                        <SelectItem value="sales_ops">Sales Ops</SelectItem>
                        <SelectItem value="financiamentos">Financiamentos</SelectItem>
                        <SelectItem value="master">Master</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={escolha.user_id || ''}
                      onValueChange={(v) => setVinculo({ ...vinculo, [p.sub]: { role: '', user_id: v } })}
                    >
                      <SelectTrigger className="w-full md:w-56">
                        <SelectValue placeholder="Ou vincular a um usuário" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => handleVincular(p.sub)}
                      disabled={vinculando === p.sub}
                      data-testid={`btn-vincular-${p.sub}`}
                    >
                      {vinculando === p.sub ? 'Aguarde' : 'Vincular'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleNegar(p.sub)}
                      disabled={vinculando === p.sub}
                      data-testid={`btn-negar-${p.sub}`}
                    >
                      Negar
                    </Button>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <Card className="overflow-hidden">
          <SheetHeader title={`Equipe · ${formatNumero(users.length)}`} />
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nome</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Vendedor no Tiny</TableHead>
                <TableHead><span className="sr-only">Ações</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-[12.5px] text-gray-500">{u.email}</p>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-300">{ROLE_LABEL[u.role] || u.role}</TableCell>
                  <TableCell className="text-[13px] tabular-nums text-gray-600 dark:text-gray-300">
                    {u.role === 'comercial' && u.tiny_vendedor_id ? `ID ${u.tiny_vendedor_id}` : ''}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenEditDialog(u)}>
                        <Edit className="mr-1.5 h-4 w-4" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, user: u })}
                        disabled={u.id === user.id}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10"
                      >
                        <Trash2 className="mr-1.5 h-4 w-4" />
                        Excluir
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </PageBody>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-h-[90vh] flex flex-col max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogDescription>Preencha os dados de acesso</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: João Silva"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="usuario@bcmed.com.br"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Senha segura"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Perfil *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="administrativo">Administrativo</SelectItem>
                  <SelectItem value="sales_ops">Sales Ops</SelectItem>
                  <SelectItem value="financiamentos">Financiamentos</SelectItem>
                  <SelectItem value="master">Master</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'comercial' && renderVendedorField('add_')}
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={saving}>
              {saving ? 'Criando...' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] flex flex-col max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
            <DialogDescription>Atualize os dados do usuário</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nome Completo *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-100 dark:bg-gray-800"
              />
              <p className="text-xs text-gray-500">Email não pode ser alterado</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-password">Nova Senha (opcional)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Deixe em branco para não alterar"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Perfil *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="administrativo">Administrativo</SelectItem>
                  <SelectItem value="sales_ops">Sales Ops</SelectItem>
                  <SelectItem value="financiamentos">Financiamentos</SelectItem>
                  <SelectItem value="master">Master</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'comercial' && renderVendedorField('edit_')}

            <div className="pt-4 border-t space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-webhook-url">URL de aviso de pagamento (sistemas)</Label>
                <Input
                  id="edit-webhook-url"
                  type="url"
                  value={formData.webhook_url || ''}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://"
                />
                <p className="text-xs text-muted-foreground">
                  Só para usuário de sistema (site, Crédito BCMED). Quando o cliente paga
                  ou o pagamento é estornado, o ERP avisa esta URL. Vale para pedidos
                  criados depois de salvar. Vazio desliga.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-webhook-segredo">Segredo do aviso</Label>
                <Input
                  id="edit-webhook-segredo"
                  type="password"
                  autoComplete="new-password"
                  value={formData.webhook_segredo || ''}
                  onChange={(e) => setFormData({ ...formData, webhook_segredo: e.target.value })}
                  placeholder={selectedUser?.webhook_segredo_definido ? 'Definido. Deixe em branco para manter' : 'Não definido'}
                />
                <p className="text-xs text-muted-foreground">
                  Assina cada aviso; o sistema confere com o mesmo segredo. Sem segredo,
                  nenhum aviso é enviado.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateUser} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => (open ? null : setDeleteDialog({ open: false, user: null }))}
        title="Excluir usuário"
        description="Esta ação é irreversível. O usuário perderá acesso ao sistema imediatamente."
        confirmText="Excluir usuário"
        onConfirm={handleConfirmDelete}
        loading={deleting}
        variant="destructive"
        details={
          deleteDialog.user
            ? [
                { label: 'Nome', value: deleteDialog.user.name },
                { label: 'Email', value: deleteDialog.user.email },
                { label: 'Perfil', value: (deleteDialog.user.role || '').toUpperCase() },
              ]
            : null
        }
      />
    </div>
  );
};

export default Users;
